import { useEffect, useMemo } from "react";
import omitBy from "lodash/omitBy";
import isEmpty from "lodash/isEmpty";

import { useAuth0V2 } from "@stadium/services/auth0";
import { useLazyQuery } from "@stadium/services/apollo-client";
import { fetchAccountStoresQuery } from "@graphql/queries/stores";
import { useCurrentUserContext } from "@stadium/hooks/lib/use-current-user";
import { useWorkspaceContext } from "@stadium/hooks/lib/workspace";
import { useFlags } from "@stadium/services/launch-darkly/use-flags";
import { workspaceDashboardFlag } from "@stadium/services/launch-darkly/constants";

const MOMENT_QUERY_VARIABLES = {
  fetchPolicy: "cache-and-network",
};

const defaultArr = [];
const defaultObj = {};

const useFetchAccountShops = ({
  queryVariableType,
  storeQuery = fetchAccountStoresQuery,
  showAccountShops = true,
} = defaultObj) => {
  const { [workspaceDashboardFlag]: workspaceDashboardFlagEnabled } = useFlags();

  const { isAuthenticated } = useAuth0V2();
  const [getAllTemplates, { data, loading }] = useLazyQuery(storeQuery, MOMENT_QUERY_VARIABLES);
  const {
    currentUserData: { accounts = defaultArr },
    loading: userLoading,
  } = useCurrentUserContext();

  const { currentWorkspaceUser, workspaceDataLoading } = useWorkspaceContext();

  const userAccountIds = currentWorkspaceUser?.user?.accountIds || defaultArr;

  const oldAccountIds = useMemo(() => accounts?.map(({ id }) => Number(id)), [accounts]);
  const newAccountIds = useMemo(() => userAccountIds.map((id) => Number(id)), [userAccountIds]);

  const accountIds = workspaceDashboardFlagEnabled ? newAccountIds : oldAccountIds;

  useEffect(() => {
    if (
      !showAccountShops ||
      (isAuthenticated &&
        ((!workspaceDashboardFlagEnabled && userLoading) ||
          (workspaceDashboardFlagEnabled && workspaceDataLoading)))
    )
      return;
    if (isAuthenticated && !userLoading) {
      getAllTemplates({
        variables: {
          accountIds,
          ...queryVariableType,
        },
      });
    } else {
      getAllTemplates({
        variables: {
          ...queryVariableType,
        },
      });
    }
  }, [
    isAuthenticated,
    userLoading,
    getAllTemplates,
    accountIds,
    queryVariableType,
    showAccountShops,
    accountIds,
    workspaceDataLoading,
    workspaceDashboardFlagEnabled,
  ]);

  const mappedAccountShops = useMemo(() => {
    let mapping = {};
    accountIds.forEach(
      (id) =>
        (mapping[id] = data?.stores?.filter(({ account }) => Number(account?.externalId) === id)),
    );
    const modifiedMapping = omitBy({ ...mapping }, isEmpty); //omit empty stores associated with IDs
    return modifiedMapping;
  }, [accountIds, data]);

  const formattedAccountShopsIds = useMemo(
    () => Object.keys(mappedAccountShops).map(Number),
    [mappedAccountShops],
  );

  const formattedAccounts = useMemo(
    () => accounts.filter(({ id }) => formattedAccountShopsIds?.includes(Number(id))),
    [accounts, formattedAccountShopsIds],
  );

  return {
    mappedAccountShops,
    formattedAccounts,
    loading,
    stores: data?.stores,
  };
};

export default useFetchAccountShops;
